import React, { useState } from 'react';
import { Trash } from 'lucide-react';
import placeholderLogo from '../../assets/unknown_company.png';
import InvestigationDetailsModal from './InvestigationDetailsModal';
import { InvestigationData } from '../../types/interfaces';
import { useTranslation } from 'react-i18next';

interface InvestigationRequestCardProps {
  investigation: InvestigationData;
  onDelete?: (id: string) => void;
}

// Se utiliza translationKey en lugar de label para obtener el texto desde el archivo de traducciones.
const statusConfig: { [key: string]: { translationKey: string; class: string } } = {
  under_review: { translationKey: 'investigationRequestCard.status.under_review', class: 'bg-yellow-100 text-yellow-800' },
  accepted: { translationKey: 'investigationRequestCard.status.accepted', class: 'bg-green-100 text-green-800' },
  rejected: { translationKey: 'investigationRequestCard.status.rejected', class: 'bg-red-100 text-red-800' },
  deleted_by_user: { translationKey: 'investigationRequestCard.status.deleted_by_user', class: 'bg-gray-100 text-gray-800' },
};

const InvestigationRequestCard: React.FC<InvestigationRequestCardProps> = ({
  investigation,
  onDelete,
}) => {
  const { t } = useTranslation();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const renderStatusMessage = () => {
    const requestDate = new Date(investigation.created_at);
    const reviewDeadline = new Date(requestDate);
    reviewDeadline.setDate(requestDate.getDate() + 2);
    const formattedReviewDeadline = reviewDeadline.toLocaleDateString();

    switch (investigation.status) {
      case 'under_review':
        return (
          <p className="text-gray-700 mt-4 text-lg text-justify">
            {t('investigationRequestCard.statusMessage.under_review', {
              company: investigation.company_name,
              deadline: formattedReviewDeadline,
            })}
          </p>
        );
      case 'accepted':
        return (
          <p className="text-gray-700 mt-4 text-lg text-justify">
            {t('investigationRequestCard.statusMessage.accepted', { company: investigation.company_name })}
          </p>
        );
      case 'rejected':
        return (
          <p className="text-gray-700 mt-4 text-lg text-justify">
            {t('investigationRequestCard.statusMessage.rejected', { company: investigation.company_name })}
          </p>
        );
      case 'deleted_by_user':
        return (
          <p className="text-gray-700 mt-4 text-lg text-justify">
            {t('investigationRequestCard.statusMessage.deleted_by_user', { company: investigation.company_name })}
          </p>
        );
      default:
        return null;
    }
  };

  const handleDeleteInvestigation = async () => {
    if (onDelete) {
      await onDelete(investigation.id);
    }
    setShowDeleteModal(false);
  };

  return (
    <div
      className="relative rounded-xl border bg-white text-gray-900 shadow hover:shadow-lg transition-shadow flex flex-col"
      style={{ minHeight: '400px' }}
    >
      {/* Icono de eliminar sólo si está en "under_review" y hay onDelete */}
      {investigation.status === 'under_review' && onDelete && (
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-red-600 transition"
          onClick={() => setShowDeleteModal(true)}
        >
          <Trash className="w-6 h-6" />
        </button>
      )}

      {/* Header */}
      <div className="flex items-start p-6">
        <img
          src={investigation.logo_url ? investigation.logo_url : placeholderLogo}
          alt={investigation.company_name}
          className="w-24 h-24 rounded-lg object-cover"
        />
        <div className="flex-1 ml-4 h-20 flex flex-col justify-between">
          <h3 className="font-bold text-2xl">
            {t('investigationRequestCard.investigationOf', { company: investigation.company_name })}
          </h3>
          <p className="text-base text-gray-500">
            {t('investigationRequestCard.requestedOn', {
              date: new Date(investigation.created_at).toLocaleDateString(),
            })}
          </p>
        </div>
      </div>

      {/* Contenido */}
      <div className="px-6 flex-1 flex flex-col justify-center">
        {renderStatusMessage()}
      </div>

      {/* Footer */}
      <div className="p-6 pt-0">
        <div
          className={`w-full inline-flex items-center justify-center rounded-md px-4 py-4 text-lg font-medium cursor-pointer ${statusConfig[investigation.status]?.class}`}
          onClick={() => setShowDetailsModal(true)}
        >
          {t(statusConfig[investigation.status]?.translationKey)}
        </div>
      </div>

      {/* Modal de detalles */}
      {showDetailsModal && (
        <InvestigationDetailsModal
          investigation={{
            ...investigation,
            comments: investigation.comments ?? '',
            created_at: new Date(investigation.created_at).toISOString(),
          }}
          logoUrl={investigation.logo_url ? investigation.logo_url : placeholderLogo}
          onClose={() => setShowDetailsModal(false)}
        />
      )}

      {/* Modal de confirmación para eliminar */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {t('investigationRequestCard.deleteConfirmationTitle')}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('investigationRequestCard.deleteConfirmationText', {
                company: investigation.company_name,
              })}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                onClick={() => setShowDeleteModal(false)}
              >
                {t('investigationRequestCard.cancel')}
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                onClick={handleDeleteInvestigation}
              >
                {t('investigationRequestCard.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestigationRequestCard;
